import React, { createContext, useContext, useState, useEffect } from 'react';
import { TOKEN_KEY, USER_KEY } from '../constants';
import { authAPI } from '../api/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,       setUser]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [activeRole, setActiveRole] = useState('');

  /* ── On mount: verify token with /me ── */
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }
      try {
        const res      = await authAPI.me();
        const userData = res.data?.data ?? res.data;
        setUser(userData);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));

        /* set default role from user data */
        const roles = userData?.roles ?? [];
        if (roles.length > 0) {
          const saved = localStorage.getItem('activeRole');
          /* restore saved role only if it's still valid for this user */
          if (saved && roles.includes(saved)) {
            setActiveRole(saved);
          } else {
            setActiveRole(roles[0]);
            localStorage.setItem('activeRole', roles[0]);
          }
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('activeRole');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  /* ── login — calls API, extracts token + user ── */
  const login = async (email, password) => {
    const res     = await authAPI.login(email, password);
    const payload = res.data?.data ?? res.data;

    const token =
      payload?.access_token ??
      payload?.token        ??
      payload?.accessToken  ??
      payload?.id_token     ??
      null;

    if (!token) throw new Error('No token received from server');

    localStorage.setItem(TOKEN_KEY, token);

    let finalUser = payload?.user ?? payload?.profile ?? null;
    if (!finalUser) {
      const meRes = await authAPI.me();
      finalUser   = meRes.data?.data ?? meRes.data;
    }

    localStorage.setItem(USER_KEY, JSON.stringify(finalUser));
    setUser(finalUser);

    /* set default role */
    const roles = finalUser?.roles ?? [];
    if (roles.length > 0) {
      setActiveRole(roles[0]);
      localStorage.setItem('activeRole', roles[0]);
    }

    return finalUser;
  };

  /* ── switchRole — persists across pages ── */
  const switchRole = (role) => {
    setActiveRole(role);
    localStorage.setItem('activeRole', role);
  };

  /* ── logout ── */
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('activeRole');
    setUser(null);
    setActiveRole('');
  };

  /* ── derived helpers ── */
  const meData  = user?.data ?? user ?? {};
  const roles   = meData?.roles ?? [];
  const isAdmin = ['admin', 'superadmin'].includes(activeRole);

  return (
    <AuthContext.Provider value={{
      user, loading,
      activeRole, switchRole,
      isAdmin, roles,
      login, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);